import React, { useState, useEffect, useRef, useCallback } from 'react';
import { contentAPI, parsePaginated } from '../services/api';
import Pagination, { PAGE_SIZE } from './Pagination';

function Workshops() {
  // State for consultations list and operations
  const [consultations, setConsultations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  
  // State for viewing details
  const [selectedConsultation, setSelectedConsultation] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  
  // State for adding new consultation
  const [showAddModal, setShowAddModal] = useState(false);
  const [newConsultation, setNewConsultation] = useState({
    title: '',
    description: '',
    youtube_video_url: '',
    imageFiles: [],
    imagePreview: []
  });
  
  // State for editing consultation
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingConsultation, setEditingConsultation] = useState(null);
  
  // State for deleting consultation
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [consultationToDelete, setConsultationToDelete] = useState(null);

  const fetchConsultations = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await contentAPI.getConsultations({
        page: currentPage,
        pageSize: PAGE_SIZE,
        search: debouncedSearch,
      });
      const parsed = parsePaginated(response.data);
      setConsultations(parsed.results);
      setTotalCount(parsed.count);
      if (parsed.results.length === 0 && parsed.count > 0 && currentPage > 1) {
        setCurrentPage((page) => Math.max(1, page - 1));
      }
    } catch (err) {
      setError('Failed to fetch consultations');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, debouncedSearch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const next = searchTerm.trim();
      setDebouncedSearch((prev) => {
        if (prev !== next) {
          setCurrentPage(1);
        }
        return next;
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    fetchConsultations();
  }, [fetchConsultations]);

  // Handle adding a new consultation
  const handleAddConsultation = async () => {
    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('title', newConsultation.title);
      formData.append('description', newConsultation.description);
      formData.append('youtube_video_url', newConsultation.youtube_video_url || '');
      
      if (newConsultation.imageFiles && newConsultation.imageFiles.length > 0) {
        newConsultation.imageFiles.forEach(file => {
          formData.append('uploaded_images', file);
        });
      }
      
      await contentAPI.addConsultation(formData);
      fetchConsultations(); // Refresh the list
      setShowAddModal(false);
      setNewConsultation({
        title: '',
        description: '',
        youtube_video_url: '',
        imageFiles: [],
        imagePreview: []
      });
    } catch (err) {
      console.error("Error adding consultation:", err);
      setError(`Failed to add consultation: ${err.response?.data?.detail || err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle edit button click
  const handleEditClick = (consultation) => {
    setEditingConsultation({
      id: consultation.id,
      title: consultation.title || '',
      description: consultation.description || '',
      youtube_video_url: consultation.youtube_video_url || '',
      images: consultation.images || [],
      imageFiles: [],
      imagePreview: []
    });
    setShowEditModal(true);
  };

  // Handle saving edited consultation
  const handleSaveEdit = async () => {
    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('title', editingConsultation.title);
      formData.append('description', editingConsultation.description);
      formData.append('youtube_video_url', editingConsultation.youtube_video_url || '');
      
      if (editingConsultation.imageFiles && editingConsultation.imageFiles.length > 0) {
        editingConsultation.imageFiles.forEach(file => {
          formData.append('uploaded_images', file);
        });
      }
      
      await contentAPI.updateConsultation(editingConsultation.id, formData);
      fetchConsultations(); // Refresh the list
      setShowEditModal(false);
    } catch (err) {
      console.error("Error updating consultation:", err);
      setError(`Failed to update consultation: ${err.response?.data?.detail || err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle delete button click
  const handleDeleteClick = (consultation) => {
    setConsultationToDelete(consultation);
    setShowDeleteModal(true);
  };

  // Handle confirming deletion
  const handleConfirmDelete = async () => {
    setIsLoading(true);
    try {
      await contentAPI.deleteConsultation(consultationToDelete.id);
      fetchConsultations(); // Refresh the list
      setShowDeleteModal(false);
    } catch (err) {
      console.error("Error deleting consultation:", err);
      setError(`Failed to delete consultation: ${err.response?.data?.detail || err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle view details click
  const handleViewDetails = (consultation) => {
    setSelectedConsultation(consultation);
    setShowViewModal(true);
  };

  // Handle image upload for new consultation
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      const previews = files.map(file => URL.createObjectURL(file));
      setNewConsultation({
        ...newConsultation,
        imageFiles: [...newConsultation.imageFiles, ...files],
        imagePreview: [...newConsultation.imagePreview, ...previews]
      });
    }
  };

  // Handle image upload for editing consultation
  const handleEditImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      const previews = files.map(file => URL.createObjectURL(file));
      setEditingConsultation({
        ...editingConsultation,
        imageFiles: [...editingConsultation.imageFiles, ...files],
        imagePreview: [...editingConsultation.imagePreview, ...previews]
      });
    }
  };

  // Helper function to extract YouTube video ID
  const getYoutubeVideoId = (url) => {
    if (!url) return null;
    
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    
    return (match && match[2].length === 11) ? match[2] : null;
  };

  // Render loading state
  if (isLoading && consultations.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-gray-800">Consultations</h1>
        <div className="flex space-x-4">
          <div className="relative">
            <input
              type="text"
              placeholder="Search consultations..."
              className="border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Add New
          </button>
        </div>
      </div>

      {/* Consultations Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {consultations.length === 0 ? (
          <div className="col-span-3 text-center py-10 text-gray-500">
            No consultations found.
          </div>
        ) : (
          consultations.map((consultation) => (
            <div key={consultation.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
              <div className="h-48 bg-gray-200 relative">
                {consultation.images && consultation.images.length > 0 ? (
                  <img 
                    src={consultation.images[0].image} 
                    alt={consultation.title} 
                    className="w-full h-full object-cover"
                  />
                ) : consultation.youtube_video_url ? (
                  <div className="w-full h-full">
                    <iframe
                      src={`https://www.youtube.com/embed/${getYoutubeVideoId(consultation.youtube_video_url)}`}
                      title={consultation.title}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="w-full h-full object-cover"
                    ></iframe>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full bg-gray-200">
                    <span className="text-gray-400">No image available</span>
                  </div>
                )}
              </div>
              <div className="p-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">{consultation.title}</h3>
                <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                  {consultation.description}
                </p>
                <div className="flex justify-between">
                  <button
                    onClick={() => handleViewDetails(consultation)}
                    className="bg-blue-100 text-blue-600 px-3 py-1 rounded hover:bg-blue-200"
                  >
                    View Details
                  </button>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEditClick(consultation)}
                      className="bg-blue-100 text-blue-600 px-3 py-1 rounded hover:bg-blue-200"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteClick(consultation)}
                      className="bg-red-100 text-red-600 px-3 py-1 rounded hover:bg-red-200"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="mt-6 rounded-lg overflow-hidden shadow">
        <Pagination page={currentPage} total={totalCount} onPageChange={setCurrentPage} />
      </div>

      {/* Add Consultation Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold mb-4">Add New Consultation</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Title *</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded"
                  value={newConsultation.title}
                  onChange={(e) => setNewConsultation({...newConsultation, title: e.target.value})}
                  required
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Description *</label>
                <textarea
                  className="w-full p-2 border rounded"
                  rows="4"
                  value={newConsultation.description}
                  onChange={(e) => setNewConsultation({...newConsultation, description: e.target.value})}
                  required
                ></textarea>
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">YouTube Video URL</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded"
                  value={newConsultation.youtube_video_url}
                  onChange={(e) => setNewConsultation({...newConsultation, youtube_video_url: e.target.value})}
                  placeholder="https://www.youtube.com/watch?v=..."
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Images</label>
                <input
                  type="file"
                  className="w-full p-2 border rounded"
                  onChange={handleImageUpload}
                  multiple
                  accept="image/*"
                />
                {newConsultation.imagePreview.length > 0 && (
                  <div className="mt-2 grid grid-cols-3 gap-2">
                    {newConsultation.imagePreview.map((preview, index) => (
                      <div key={index} className="relative h-20 bg-gray-100 rounded">
                        <img src={preview} alt="Preview" className="h-full w-full object-cover rounded" />
                        <button
                          className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1 transform translate-x-1/3 -translate-y-1/3"
                          onClick={() => {
                            const newPreviews = [...newConsultation.imagePreview];
                            const newFiles = [...newConsultation.imageFiles];
                            newPreviews.splice(index, 1);
                            newFiles.splice(index, 1);
                            setNewConsultation({
                              ...newConsultation,
                              imagePreview: newPreviews,
                              imageFiles: newFiles
                            });
                          }}
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end mt-6">
              <button
                className="bg-gray-300 text-gray-800 px-4 py-2 rounded mr-2 hover:bg-gray-400"
                onClick={() => setShowAddModal(false)}
              >
                Cancel
              </button>
              <button
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                onClick={handleAddConsultation}
                disabled={!newConsultation.title || !newConsultation.description || isLoading}
              >
                {isLoading ? 'Adding...' : 'Add Consultation'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Consultation Modal */}
      {showEditModal && editingConsultation && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold mb-4">Edit Consultation</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Title *</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded"
                  value={editingConsultation.title}
                  onChange={(e) => setEditingConsultation({...editingConsultation, title: e.target.value})}
                  required
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Description *</label>
                <textarea
                  className="w-full p-2 border rounded"
                  rows="4"
                  value={editingConsultation.description}
                  onChange={(e) => setEditingConsultation({...editingConsultation, description: e.target.value})}
                  required
                ></textarea>
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">YouTube Video URL</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded"
                  value={editingConsultation.youtube_video_url}
                  onChange={(e) => setEditingConsultation({...editingConsultation, youtube_video_url: e.target.value})}
                  placeholder="https://www.youtube.com/watch?v=..."
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Current Images</label>
                {editingConsultation.images && editingConsultation.images.length > 0 ? (
                  <div className="grid grid-cols-3 gap-2 mb-2">
                    {editingConsultation.images.map((img, index) => (
                      <div key={index} className="relative h-20 bg-gray-100 rounded">
                        <img src={img.image} alt={`Image ${index}`} className="h-full w-full object-cover rounded" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No images currently uploaded</p>
                )}
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Add New Images</label>
                <input
                  type="file"
                  className="w-full p-2 border rounded"
                  onChange={handleEditImageUpload}
                  multiple
                  accept="image/*"
                />
                {editingConsultation.imagePreview && editingConsultation.imagePreview.length > 0 && (
                  <div className="mt-2 grid grid-cols-3 gap-2">
                    {editingConsultation.imagePreview.map((preview, index) => (
                      <div key={index} className="relative h-20 bg-gray-100 rounded">
                        <img src={preview} alt="Preview" className="h-full w-full object-cover rounded" />
                        <button
                          className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1 transform translate-x-1/3 -translate-y-1/3"
                          onClick={() => {
                            const newPreviews = [...editingConsultation.imagePreview];
                            const newFiles = [...editingConsultation.imageFiles];
                            newPreviews.splice(index, 1);
                            newFiles.splice(index, 1);
                            setEditingConsultation({
                              ...editingConsultation,
                              imagePreview: newPreviews,
                              imageFiles: newFiles
                            });
                          }}
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end mt-6">
              <button
                className="bg-gray-300 text-gray-800 px-4 py-2 rounded mr-2 hover:bg-gray-400"
                onClick={() => setShowEditModal(false)}
              >
                Cancel
              </button>
              <button
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                onClick={handleSaveEdit}
                disabled={!editingConsultation.title || !editingConsultation.description || isLoading}
              >
                {isLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Consultation Modal */}
      {showDeleteModal && consultationToDelete && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold mb-4">Confirm Deletion</h2>
            <p className="text-gray-700 mb-4">Are you sure you want to delete this consultation?</p>
            <div className="flex justify-end">
              <button
                className="bg-gray-300 text-gray-800 px-4 py-2 rounded mr-2 hover:bg-gray-400"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
              <button
                className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
                onClick={handleConfirmDelete}
                disabled={isLoading}
              >
                {isLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showViewModal && selectedConsultation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-2xl font-bold text-gray-800">{selectedConsultation.title}</h2>
                <button 
                  onClick={() => setShowViewModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {/* Consultation Images */}
              {selectedConsultation.images && selectedConsultation.images.length > 0 && (
                <div className="mb-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedConsultation.images.map((img, index) => (
                      <div key={index} className="h-64 bg-gray-200 rounded-lg overflow-hidden">
                        <img 
                          src={img.image} 
                          alt={`${selectedConsultation.title} - ${index + 1}`} 
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* YouTube Video */}
              {selectedConsultation.youtube_video_url && (
                <div className="mb-6 aspect-w-16 aspect-h-9">
                  <iframe
                    src={`https://www.youtube.com/embed/${getYoutubeVideoId(selectedConsultation.youtube_video_url)}`}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-64"
                  ></iframe>
                </div>
              )}
              
              {/* Description */}
              <div className="prose max-w-none">
                <p>{selectedConsultation.description}</p>
              </div>
              
              <div className="mt-6 text-right">
                <button
                  onClick={() => setShowViewModal(false)}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-md text-sm transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Helper function to extract YouTube video ID
const getYoutubeVideoId = (url) => {
  if (!url) return null;
  
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  
  return (match && match[2].length === 11) ? match[2] : null;
};

export default Workshops;



